import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, FileText, Calendar, Users, Star, Clock, Shield, Target, Home, Globe2, Briefcase, ArrowUpRight, Building2, Calculator } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ServiceCard from '@/components/ServiceCard';
import { useAuth } from '@/contexts/AuthContext';
import { usePayment } from '@/hooks/usePayment';
import '@/styles/animations.css';

const Index = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const features = [
    {
      title: "Expert Guidance",
      description: "Our experienced team provides personalized guidance throughout your application process",
      icon: Target
    },
    {
      title: "Fast Processing",
      description: "Streamlined procedures to minimize delays and get your application submitted quickly",
      icon: Clock
    },
    {
      title: "High Success Rate",
      description: "95%+ approval rate for applications we handle from start to finish",
      icon: Star
    },
    {
      title: "24/7 Support",
      description: "Round-the-clock support to answer your questions and address concerns",
      icon: Users
    }
  ];

  // Main services overview
  const mainServices = [
    {
      title: "Temporary Residence Card",
      description: "Legal residence in Poland for up to 3 years with comprehensive support",
      icon: Clock,
      color: "from-blue-500/10 to-blue-600/10",
      iconColor: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200"
    },
    {
      title: "Permanent Residence Card",
      description: "Secure your long-term future in Poland with expert assistance",
      icon: Home,
      color: "from-green-500/10 to-green-600/10",
      iconColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200"
    },
    {
      title: "EU Long-term Residence Card",
      description: "Freedom to live and work across the EU with special residence status",
      icon: Globe2,
      color: "from-purple-500/10 to-purple-600/10",
      iconColor: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200"
    },
    {
      title: "Work Permit",
      description: "Fast-track your career in Poland with efficient work permit service",
      icon: Briefcase,
      color: "from-orange-500/10 to-orange-600/10",
      iconColor: "text-orange-600",
      bgColor: "bg-orange-50",
      borderColor: "border-orange-200"
    }
  ];

  // Standalone services
  const standaloneServices = [
    {
      title: "Company Registration",
      description: "Transform your business idea into reality in Poland",
      icon: FileText,
      color: "from-indigo-500/10 to-indigo-600/10",
      iconColor: "text-indigo-600"
    },
    {
      title: "Book keeping",
      description: "Professional bookkeeping services for your business",
      icon: Calculator,
      color: "from-emerald-500/10 to-emerald-600/10",
      iconColor: "text-emerald-600"
    },
    {
      title: "Business Incubator",
      description: "Launch your business venture with comprehensive support",
      icon: Building2,
      color: "from-teal-500/10 to-teal-600/10",
      iconColor: "text-teal-600"
    },
    {
      title: "Translation Services",
      description: "Professional document translation for all your needs",
      icon: FileText,
      color: "from-pink-500/10 to-pink-600/10",
      iconColor: "text-pink-600"
    }
  ];

  const services = [
    {
      title: "Free Consultation",
      description: "Get started with basic guidance and document checklist",
      price: "Free",
      features: [
        "Initial consultation call",
        "Basic document checklist", 
        "General guidance",
        "Email support"
      ],
      isFree: true
    },
    {
      title: "Basic Support",
      description: "Document review and basic consultation",
      price: "1299 PLN",
      features: [
        "Document review",
        "Extended consultation (45 min)",
        "Application checklist",
        "Email support",
        "Document preparation guide"
      ]
    },
    {
      title: "Standard Support",
      description: "Comprehensive application assistance",
      price: "2199 PLN",
      features: [
        "Complete document review",
        "Form completion assistance",
        "Extended consultation (90 min)",
        "Document preparation guide",
        "Priority email support",
        "Application submission guidance",
        "Progress tracking"
      ],
      popular: true
    },
    {
      title: "Premium Support",
      description: "Full-service application management",
      price: "3499 PLN",
      features: [
        "End-to-end application management",
        "Document collection and review",
        "Form completion service",
        "Appointment scheduling assistance",
        "Unlimited consultations",
        "Phone and email support",
        "Application tracking",
        "Follow-up services",
        "Dedicated case manager"
      ]
    }
  ];

  const allServices = [
    {
      title: "Temporary Residence Card",
      subtitle: "Legal residence in Poland for up to 3 years",
      description: "Obtain legal residence in Poland for up to 3 years. Our comprehensive service includes document preparation, application submission, and guidance throughout the entire process to ensure a smooth transition to your life in Poland.",
      type: 'main',
      image: '/img/trc.jpeg',
      icon: undefined,
    },
    {
      title: "Permanent Residence Card",
      subtitle: "Secure your long-term future in Poland",
      description: "Secure your long-term future in Poland with our expert assistance in obtaining permanent residency. We handle the complex documentation and requirements, ensuring your application meets all criteria for approval.",
      type: 'main',
      image: '/img/prc.jpeg',
      icon: undefined,
    },
    {
      title: "EU Long-term Residence Card",
      subtitle: "Freedom to live and work across the EU",
      description: "Gain the freedom to live and work across the EU with this special residence status. We guide you through the qualification requirements and application process, opening doors to opportunities throughout Europe.",
      type: 'main',
      image: '/img/EULT.jpeg',
      icon: undefined,
    },
    {
      title: "Work Permit",
      subtitle: "Fast-track your career in Poland",
      description: "Fast-track your career in Poland with our efficient work permit service. We collaborate with employers and authorities to secure your legal right to work, handling all necessary documentation and compliance requirements.",
      type: 'main',
      image: '/img/workpermit.jpeg',
      icon: undefined,
    },
    {
      title: "Company Registration",
      subtitle: "Transform your business idea into reality in Poland",
      description: "Transform your business idea into reality with our company registration service. We handle all legal requirements, tax registrations, and administrative procedures to establish your company successfully in Poland.",
      type: 'additional',
      image: '/img/companyregistration.jpeg',
      icon: undefined,
    },
    {
      title: "Book keeping",
      subtitle: "Professional bookkeeping services for your business",
      description: "Professional bookkeeping services for your business in Poland. We handle all your accounting needs, tax compliance, financial reporting, and ensure your business meets all regulatory requirements.",
      type: 'additional',
      image: '/img/booking keeping.jpg',
      icon: undefined,
    },
    {
      title: "Business Incubator",
      subtitle: "Launch your business venture with comprehensive support",
      description: "Launch your business venture in Poland with our comprehensive incubator program. We provide essential support services, legal guidance, and networking opportunities to help your business thrive in the Polish market.",
      type: 'additional',
      image: '/img/businessincubator.jpeg',
      icon: undefined,
    },
    {
      title: "Translation Services",
      subtitle: "Professional document translation for all your needs",
      description: "Professional document translation for all your immigration needs. We work with certified translators to ensure all your documents meet official requirements.",
      type: 'additional',
      image: '/img/translationservice.jpeg',
      icon: undefined,
    },
  ];

  const [selectedServiceIndex, setSelectedServiceIndex] = useState(0);
  const selectedService = allServices[selectedServiceIndex];

  const handleServiceSelect = (serviceTitle: string) => {
    setSelectedPlan(serviceTitle);
    if (serviceTitle === "Free Consultation") {
      // Always redirect to consultation page, regardless of authentication status
      navigate('/consultation');
    } else {
      // For other services, require authentication
      if (!user) {
        navigate('/register');
        return;
      }
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sigma-grey via-white to-sigma-grey">
      <Header />
      
      {/* Hero Section - increased height */}
      <section className="py-24 sm:py-60 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-60 sm:w-80 h-60 sm:h-80 bg-sigma-red opacity-10 rounded-full animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-60 sm:w-80 h-60 sm:h-80 bg-sigma-dark-red opacity-10 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-6xl font-alumni font-bold text-sigma-black mb-4 sm:mb-6 animate-fade-in">
            Your Complete Business & Immigration Partner in Poland
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 max-w-3xl mx-auto animate-fade-in font-hind" style={{ animationDelay: '500ms' }}>
            Navigate your business and immigration journey in Poland with confidence. Our expert team provides 
            comprehensive solutions for company registration, residence cards, work permits, and business services.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-3 sm:space-y-0 sm:space-x-4 animate-fade-in" style={{ animationDelay: '700ms' }}>
            <Link to={user ? "/dashboard" : "/register"} className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-sigma-red to-sigma-dark-red hover:from-sigma-dark-red hover:to-sigma-red transition-all duration-300 hover:scale-105 shadow-lg text-white font-hind">
                Start Your Application
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Interactive Services Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto mb-10">
          <h2 className="text-3xl sm:text-4xl font-alumni font-bold text-sigma-black text-center mb-2 animate-fade-in">Our Services</h2>
          <p className="text-lg sm:text-xl text-gray-600 text-center font-hind animate-fade-in" style={{ animationDelay: '200ms' }}>
            Explore our main immigration and business services
          </p>
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-stretch">
          {/* Left: Accordion List */}
          <div className="flex flex-col gap-4">
            {/* Main Services Accordion */}
            {allServices.filter(s => s.type === 'main').map((service, idx) => (
              <div
                key={service.title}
                className={`rounded-lg p-6 transition-all duration-300 cursor-pointer bg-sigma-grey/10 border-2 ${selectedServiceIndex === idx ? 'border-sigma-red bg-sigma-red/10' : 'border-transparent hover:border-sigma-red/40'}`}
                onClick={() => setSelectedServiceIndex(idx)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-sigma-red mb-1">{service.title}</h3>
                    <p className="text-sm text-sigma-black font-hind mb-2">{service.subtitle}</p>
                  </div>
                  <span className="text-sigma-red text-2xl font-bold">{selectedServiceIndex === idx ? '-' : '+'}</span>
                </div>
                {selectedServiceIndex === idx && (
                  <div className="mt-2 text-sigma-black text-base font-hind">
                    <p>{service.description}</p>
                    <button 
                      className="mt-2 text-sigma-red font-semibold underline" 
                      onClick={(e) => {
                        e.stopPropagation();
                        if (service.title === "Temporary Residence Card") {
                          navigate('/temporary-residence-card');
                        } else if (service.title === "Permanent Residence Card") {
                          navigate('/permanent-residence-card');
                        } else if (service.title === "EU Long-term Residence Card") {
                          navigate('/eu-long-term-residence-card');
                        } else if (service.title === "Work Permit") {
                          navigate('/work-permit');
                        } else {
                          navigate('/services');
                        }
                      }}
                    >
                      Learn More
                    </button>
                  </div>
                )}
              </div>
            ))}
            {/* Additional Services Accordion */}
            {allServices.filter(s => s.type === 'additional').map((service, idx) => {
              const realIdx = 4 + idx;
              return (
                <div
                  key={service.title}
                  className={`rounded-lg p-6 transition-all duration-300 cursor-pointer bg-sigma-grey/10 border-2 ${selectedServiceIndex === realIdx ? 'border-sigma-red bg-sigma-red/10' : 'border-transparent hover:border-sigma-red/40'}`}
                  onClick={() => setSelectedServiceIndex(realIdx)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-bold text-sigma-black mb-1">{service.title}</h3>
                      <p className="text-sm text-sigma-black font-hind mb-2">{service.subtitle}</p>
                    </div>
                    <span className="text-sigma-red text-2xl font-bold">{selectedServiceIndex === realIdx ? '-' : '+'}</span>
                  </div>
                  {selectedServiceIndex === realIdx && (
                    <div className="mt-2 text-sigma-black text-base font-hind">
                      <p>{service.description}</p>
                      <button 
                        className="mt-2 text-sigma-red font-semibold underline" 
                        onClick={(e) => {
                          e.stopPropagation();
                          if (service.title === "Company Registration") {
                            navigate('/company-registration');
                          } else if (service.title === "Book keeping") {
                            navigate('/book-keeping');
                          } else if (service.title === "Business Incubator") {
                            navigate('/business-incubator');
                          } else if (service.title === "Translation Services") {
                            navigate('/translation-services');
                          } else {
                            navigate('/contact');
                          }
                        }}
                      >
                        Learn More
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Right: Image and Navigation */}
          <div className="flex flex-col h-full justify-between items-center bg-sigma-grey/10 rounded-lg p-6 relative">
            <div className="flex-1 flex items-center justify-center w-full h-80">
              {selectedService.image ? (
                <img
                  src={selectedService.image}
                  alt={selectedService.title}
                  className="object-cover lg:object-contain rounded-lg w-full h-full max-h-80 lg:max-h-[400px] shadow-md"
                />
              ) : selectedService.icon ? (
                <selectedService.icon className="w-32 h-32 text-sigma-red drop-shadow-lg" />
              ) : null}
            </div>
            <div className="flex items-center justify-between w-full mt-6">
              <button
                className="text-sigma-red text-2xl font-bold px-4 py-2 hover:bg-sigma-red/10 rounded-full transition"
                onClick={() => setSelectedServiceIndex((selectedServiceIndex + allServices.length - 1) % allServices.length)}
                aria-label="Previous Service"
              >
                &#8592;
              </button>
              <span className="text-sigma-black font-hind text-lg">{String(selectedServiceIndex + 1).padStart(2, '0')}/{String(allServices.length).padStart(2, '0')}</span>
              <button
                className="text-sigma-red text-2xl font-bold px-4 py-2 hover:bg-sigma-red/10 rounded-full transition"
                onClick={() => setSelectedServiceIndex((selectedServiceIndex + 1) % allServices.length)}
                aria-label="Next Service"
              >
                &#8594;
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-sigma-grey to-white relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-alumni font-bold text-sigma-black mb-3 sm:mb-4 animate-fade-in">Why Choose Sigma Immigration?</h2>
            <p className="text-lg sm:text-xl text-gray-600 animate-fade-in font-hind" style={{ animationDelay: '200ms' }}>
              We make the complex immigration process simple and stress-free
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 animate-fade-in" style={{ animationDelay: `${index * 150}ms` }}>
                  <CardHeader className="space-y-4 p-4 sm:p-6">
                    <div className="mx-auto bg-gradient-to-br from-sigma-red to-sigma-dark-red p-3 rounded-full w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mb-2 sm:mb-4 animate-scale-in" style={{ animationDelay: `${index * 150 + 200}ms` }}>
                      <IconComponent className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                    </div>
                    <CardTitle className="text-lg sm:text-xl font-alumni font-bold text-sigma-black">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6">
                    <CardDescription className="text-gray-600 text-sm sm:text-base font-hind">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-alumni font-bold text-sigma-black mb-3 sm:mb-4 animate-fade-in">Choose Your Support Package</h2>
            <p className="text-lg sm:text-xl text-gray-600 animate-fade-in font-hind" style={{ animationDelay: '200ms' }}>
              Select the level of support that best fits your needs
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-6">
            {services.map((service, index) => (
              <div 
                key={index} 
                className={`animate-scale-in relative ${
                  service.popular ? 'transform hover:scale-105 lg:-translate-y-4' : 'transform hover:scale-105'
                }`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <ServiceCard 
                  {...service}
                  className={`h-full ${
                    service.popular ? 'border-2 border-primary shadow-xl' : 'border border-border'
                  }`}
                  onSelect={() => {
                    if (!user) {
                      navigate('/register');
                      return;
                    }
                    handleServiceSelect(service.title);
                  }}
                  isSelected={selectedPlan === service.title}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-sigma-red to-sigma-dark-red relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-24 sm:w-32 h-24 sm:h-32 bg-white opacity-10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-32 sm:w-48 h-32 sm:h-48 bg-white opacity-5 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl font-alumni font-bold text-white mb-4 sm:mb-6 animate-fade-in">
            Ready to Start Journey in Poland?
          </h2>
          <p className="text-lg sm:text-xl text-white opacity-90 mb-6 sm:mb-8 animate-fade-in font-hind" style={{ animationDelay: '200ms' }}>
            Join hundreds of satisfied clients who have successfully established their business and immigration presence in Poland with our help.
          </p>
          <Link to={user ? "/dashboard" : "/register"} className="block sm:inline-block">
            <Button 
              size="lg" 
              variant="secondary" 
              className="w-full sm:w-auto transition-all duration-300 hover:scale-105 shadow-lg animate-fade-in bg-white text-sigma-red hover:bg-sigma-grey font-hind" 
              style={{ animationDelay: '400ms' }}
            >
              Get Started Today
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
